import type { PresupuestoObra } from './tipos'

const formatearCOP = (val: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(val || 0)

/**
 * Abre una ventana emergente estilizada lista para imprimir en PDF
 * con membrete corporativo MEGA, capítulos, desglose AIU y firmas de ingeniería.
 */
export function imprimirPresupuestoPdf(presupuesto: PresupuestoObra) {
  const aiu = presupuesto.aiu || {
    porcentaje_admin: 10,
    porcentaje_imprevistos: 0,
    porcentaje_utilidad: 3,
    porcentaje_iva_utilidad: 19,
  }

  const cDirecto = presupuesto.costo_directo_total
  const valAdmin = cDirecto * ((aiu.porcentaje_admin || 0) / 100)
  const valImprev = cDirecto * ((aiu.porcentaje_imprevistos || 0) / 100)
  const valUtil = cDirecto * ((aiu.porcentaje_utilidad || 0) / 100)
  const valIvaUtil = valUtil * ((aiu.porcentaje_iva_utilidad || 19) / 100)

  let capitulosHtml = ''
  presupuesto.capitulos.forEach((cap, capIdx) => {
    let itemsRows = ''
    cap.items.forEach((it, itIdx) => {
      itemsRows += `
        <tr>
          <td style="text-align: center; color: #64748b; font-size: 11px;">${capIdx + 1}.${itIdx + 1}</td>
          <td style="font-family: monospace; font-size: 11px; font-weight: 600; color: #4338ca;">${it.codigo}</td>
          <td style="font-size: 11px; color: #1e293b; line-height: 1.4;">${it.nombre}</td>
          <td style="text-align: center; font-size: 11px; font-weight: 600;">${it.unidad}</td>
          <td style="text-align: right; font-size: 11px;">${it.cantidad.toLocaleString('es-CO')}</td>
          <td style="text-align: right; font-size: 11px;">${formatearCOP(it.precio_unitario)}</td>
          <td style="text-align: right; font-size: 11px; font-weight: 600; color: #0f172a;">${formatearCOP(it.subtotal)}</td>
        </tr>
      `
    })

    capitulosHtml += `
      <div style="margin-bottom: 24px;">
        <div style="background-color: #312e81; color: #ffffff; padding: 6px 12px; font-weight: 700; font-size: 12px; border-radius: 4px 4px 0 0; display: flex; justify-content: space-between;">
          <span>${cap.nombre.toUpperCase()}</span>
          <span>SUBTOTAL: ${formatearCOP(cap.subtotal_capitulo)}</span>
        </div>
        <table style="width: 100%; border-collapse: collapse; font-family: sans-serif; font-size: 11px; border: 1px solid #e2e8f0; border-top: none;">
          <thead>
            <tr style="background-color: #f8fafc; border-bottom: 2px solid #cbd5e1; color: #475569; font-size: 10px; text-transform: uppercase;">
              <th style="padding: 6px 8px; width: 45px; text-align: center;">Ítem</th>
              <th style="padding: 6px 8px; width: 95px; text-align: left;">Cód. APU</th>
              <th style="padding: 6px 8px; text-align: left;">Descripción de Actividad</th>
              <th style="padding: 6px 8px; width: 45px; text-align: center;">Und</th>
              <th style="padding: 6px 8px; width: 65px; text-align: right;">Cant</th>
              <th style="padding: 6px 8px; width: 95px; text-align: right;">V. Unitario</th>
              <th style="padding: 6px 8px; width: 105px; text-align: right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${itemsRows}
          </tbody>
        </table>
      </div>
    `
  })

  const html = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="utf-8">
      <title>Presupuesto ${presupuesto.codigo} - MEGA DISTRIBUCIONES</title>
      <style>
        @page { size: letter portrait; margin: 15mm; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1e293b; margin: 0; padding: 20px; font-size: 12px; }
        table th, table td { padding: 5px 8px; border-bottom: 1px solid #f1f5f9; }
        .no-print { display: flex; gap: 10px; margin-bottom: 20px; }
        @media print {
          .no-print { display: none !important; }
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="no-print">
        <button onclick="window.print()" style="background: #4338ca; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-weight: 600;">
          Imprimir / Guardar en PDF
        </button>
        <button onclick="window.close()" style="background: #e2e8f0; color: #334155; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">
          Cerrar
        </button>
      </div>

      <!-- Membrete -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #4338ca; padding-bottom: 12px; margin-bottom: 16px;">
        <div>
          <h1 style="margin: 0; font-size: 20px; color: #4338ca; font-weight: 900; letter-spacing: -0.5px;">MEGA DISTRIBUCIONES S.A.S.</h1>
          <p style="margin: 2px 0 0; font-size: 11px; color: #64748b;">NIT: 900.542.891-4 • Soluciones Integrales de Redes Eléctricas y Civiles</p>
          <p style="margin: 2px 0 0; font-size: 11px; color: #64748b;">Girón - Santander • contacto@megadistribuciones.com</p>
        </div>
        <div style="text-align: right;">
          <div style="font-size: 16px; font-weight: 800; color: #0f172a;">${presupuesto.codigo}</div>
          <div style="font-size: 11px; color: #64748b; margin-top: 2px;">Fecha: ${new Date(presupuesto.updated_at || presupuesto.created_at).toLocaleDateString('es-CO')}</div>
          <div style="display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 700; background: #e0e7ff; color: #3730a3; margin-top: 4px;">
            ${presupuesto.estado.toUpperCase()}
          </div>
        </div>
      </div>

      <!-- Ficha Técnica de la Obra -->
      <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; margin-bottom: 20px; display: grid; grid-template-columns: 2fr 1fr; gap: 8px; font-size: 11px;">
        <div><strong>Proyecto:</strong> ${presupuesto.nombre}</div>
        <div><strong>Cliente:</strong> ${presupuesto.cliente || 'GENERAL'}</div>
        <div><strong>Ubicación:</strong> ${presupuesto.ubicacion || 'Bucaramanga, Santander'}</div>
        <div><strong>Plazo Estimado:</strong> 60 a 90 días calendario</div>
      </div>

      <!-- Capítulos -->
      ${capitulosHtml}

      <!-- AIU y Gran Total -->
      <div style="display: flex; justify-content: flex-end; margin-top: 16px; page-break-inside: avoid;">
        <table style="width: 320px; border-collapse: collapse; font-size: 11px;">
          <tr>
            <td style="color: #64748b; text-align: right; padding: 4px 8px;">Costo Directo Total:</td>
            <td style="text-align: right; font-weight: 600; padding: 4px 8px;">${formatearCOP(cDirecto)}</td>
          </tr>
          <tr>
            <td style="color: #64748b; text-align: right; padding: 4px 8px;">Administración (${aiu.porcentaje_admin}%):</td>
            <td style="text-align: right; padding: 4px 8px;">${formatearCOP(valAdmin)}</td>
          </tr>
          <tr>
            <td style="color: #64748b; text-align: right; padding: 4px 8px;">Imprevistos (${aiu.porcentaje_imprevistos}%):</td>
            <td style="text-align: right; padding: 4px 8px;">${formatearCOP(valImprev)}</td>
          </tr>
          <tr>
            <td style="color: #64748b; text-align: right; padding: 4px 8px;">Utilidad (${aiu.porcentaje_utilidad}%):</td>
            <td style="text-align: right; padding: 4px 8px;">${formatearCOP(valUtil)}</td>
          </tr>
          <tr>
            <td style="color: #64748b; text-align: right; padding: 4px 8px;">IVA s/ Utilidad (${aiu.porcentaje_iva_utilidad}%):</td>
            <td style="text-align: right; padding: 4px 8px;">${formatearCOP(valIvaUtil)}</td>
          </tr>
          <tr style="border-top: 2px solid #0f172a; background-color: #f1f5f9;">
            <td style="font-weight: 800; font-size: 13px; color: #0f172a; text-align: right; padding: 8px;">TOTAL PRESUPUESTO:</td>
            <td style="font-weight: 800; font-size: 14px; color: #059669; text-align: right; padding: 8px;">${formatearCOP(presupuesto.total_presupuesto)}</td>
          </tr>
        </table>
      </div>

      <!-- Firmas -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 50px; page-break-inside: avoid;">
        <div style="border-top: 1px solid #94a3b8; padding-top: 8px; text-align: center;">
          <p style="margin: 0; font-weight: 700; font-size: 11px;">ING. RESIDENTE DE PRESUPUESTOS</p>
          <p style="margin: 2px 0 0; font-size: 10px; color: #64748b;">MEGA DISTRIBUCIONES S.A.S.</p>
        </div>
        <div style="border-top: 1px solid #94a3b8; padding-top: 8px; text-align: center;">
          <p style="margin: 0; font-weight: 700; font-size: 11px;">ACEPTADO POR CLIENTE / CONTRATANTE</p>
          <p style="margin: 2px 0 0; font-size: 10px; color: #64748b;">${presupuesto.cliente || 'Firma y C.C.'}</p>
        </div>
      </div>
    </body>
    </html>
  `

  const win = window.open('', '_blank', 'width=900,height=800')
  if (win) {
    win.document.open()
    win.document.write(html)
    win.document.close()
  }
}

/**
 * Genera el enlace de WhatsApp con el mensaje estructurado para envío directo al cliente
 */
export function generarEnlaceWhatsAppPresupuesto(presupuesto: PresupuestoObra, telefonoCliente?: string): string {
  const formatearCOP = (val: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0)

  const mensaje = `Hola Estimado(a) Cliente,
Desde MEGA DISTRIBUCIONES S.A.S. le compartimos la cotización oficial de ingeniería:

📋 *Presupuesto:* ${presupuesto.codigo}
🏢 *Proyecto:* ${presupuesto.nombre}
📍 *Ubicación:* ${presupuesto.ubicacion || 'Bucaramanga, Santander'}
📊 *Capítulos cotizados:* ${presupuesto.capitulos.length}
💰 *Total Presupuesto (con AIU):* ${formatearCOP(presupuesto.total_presupuesto)}

Quedamos a su entera disposición para cualquier ajuste o visita técnica.`

  const baseUrl = telefonoCliente
    ? `https://wa.me/${telefonoCliente.replace(/[^0-9]/g, '')}`
    : 'https://api.whatsapp.com/send'

  return `${baseUrl}?text=${encodeURIComponent(mensaje)}`
}

